/**
 * ScrapeKing Design System v2 - Button Components
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC, ButtonHTMLAttributes } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 15px rgba(0, 200, 224, 0.3);
  }
  50% {
    box-shadow: 0 0 25px rgba(0, 200, 224, 0.5);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

// ═══════════════════════════════════════════════════════════════
// SIZE STYLES
// ═══════════════════════════════════════════════════════════════

const sizeStyles = {
  sm: css`
    height: 32px;
    padding: 0 var(--sk-space-3);
    font-size: var(--sk-text-xs);
    gap: var(--sk-space-2);
  `,
  md: css`
    height: 40px;
    padding: 0 var(--sk-space-4);
    font-size: var(--sk-text-sm);
    gap: var(--sk-space-2);
  `,
  lg: css`
    height: 48px;
    padding: 0 var(--sk-space-6);
    font-size: var(--sk-text-base);
    gap: var(--sk-space-3);
  `,
};

// ═══════════════════════════════════════════════════════════════
// VARIANT STYLES
// ═══════════════════════════════════════════════════════════════

const variantStyles = {
  primary: css`
    background: linear-gradient(135deg, var(--sk-cyan-600) 0%, var(--sk-cyan-700) 100%);
    color: #ffffff;
    border: 1px solid var(--sk-cyan-500);
    box-shadow: 0 0 20px rgba(0, 200, 224, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--sk-cyan-500) 0%, var(--sk-cyan-600) 100%);
      box-shadow: 0 0 30px rgba(0, 200, 224, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15);
      transform: translateY(-1px);
      color: #ffffff;
    }

    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 0 15px rgba(0, 200, 224, 0.3);
    }

    &:focus-visible {
      animation: ${pulseGlow} 1.5s ease infinite;
    }
  `,

  secondary: css`
    background: var(--sk-surface-default);
    color: var(--sk-text-primary);
    border: 1px solid var(--sk-surface-border);

    &:hover:not(:disabled) {
      background: var(--sk-surface-hover);
      border-color: var(--sk-surface-border-hover);
      transform: translateY(-1px);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
      background: var(--sk-surface-active);
    }
  `,

  ghost: css`
    background: transparent;
    color: var(--sk-text-secondary);
    border: 1px solid transparent;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.05);
      color: var(--sk-text-primary);
      border-color: var(--sk-surface-border);
    }

    &:active:not(:disabled) {
      background: rgba(255, 255, 255, 0.08);
    }
  `,

  danger: css`
    background: linear-gradient(135deg, var(--sk-red-600) 0%, var(--sk-red-700) 100%);
    color: #ffffff;
    border: 1px solid var(--sk-red-500);
    box-shadow: 0 0 20px rgba(255, 0, 51, 0.2);

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--sk-red-500) 0%, var(--sk-red-600) 100%);
      box-shadow: 0 0 30px rgba(255, 0, 51, 0.4);
      transform: translateY(-1px);
      color: #ffffff;
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,

  success: css`
    background: linear-gradient(135deg, var(--sk-green-700) 0%, var(--sk-green-800) 100%);
    color: #ffffff;
    border: 1px solid var(--sk-green-600);
    box-shadow: 0 0 20px rgba(0, 255, 127, 0.15);

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--sk-green-600) 0%, var(--sk-green-700) 100%);
      box-shadow: 0 0 30px rgba(0, 255, 127, 0.3);
      transform: translateY(-1px);
      color: #ffffff;
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,
};

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $loading: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--sk-font-body);
  font-weight: 600;
  border-radius: var(--sk-radius-default);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  white-space: nowrap;

  ${props => sizeStyles[props.$size]}
  ${props => variantStyles[props.$variant]}
  ${props => props.$fullWidth && 'width: 100%;'}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  ${props => props.$loading && css`
    color: transparent !important;
    pointer-events: none;
  `}

  /* Shimmer effect on hover for primary */
  ${props => props.$variant === 'primary' && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover::before {
      opacity: 1;
      animation: ${shimmer} 1.5s ease infinite;
    }
  `}
`;

const LoadingSpinner = styled.span`
  position: absolute;
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const IconWrapper = styled.span<{ $position: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 18px;
    height: 18px;
  }
`;

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export const Button: FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  children,
  disabled,
  ...props
}) => {
  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $loading={loading}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {icon && iconPosition === 'left' && <IconWrapper $position="left">{icon}</IconWrapper>}
      {children}
      {icon && iconPosition === 'right' && <IconWrapper $position="right">{icon}</IconWrapper>}
    </StyledButton>
  );
};

// ═══════════════════════════════════════════════════════════════
// ICON BUTTON
// ═══════════════════════════════════════════════════════════════

const StyledIconButton = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--sk-radius-default);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  aspect-ratio: 1;

  ${props => props.$size === 'sm' && 'width: 32px; height: 32px;'}
  ${props => props.$size === 'md' && 'width: 40px; height: 40px;'}
  ${props => props.$size === 'lg' && 'width: 48px; height: 48px;'}

  ${props => variantStyles[props.$variant]}

  svg {
    width: 20px;
    height: 20px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon: React.ReactNode;
  'aria-label': string;
}

export const IconButton: FC<IconButtonProps> = ({
  variant = 'ghost',
  size = 'md',
  icon,
  ...props
}) => {
  return (
    <StyledIconButton $variant={variant} $size={size} {...props}>
      {icon}
    </StyledIconButton>
  );
};

export default Button;
